<template>
  <v-tooltip top :disabled="!canOnlyChangeStock">
    <template #activator="{ on, attrs }">
      <v-card
        class="variant-card mb-4"
        outlined
        v-bind="attrs"
        v-on="on"
        @click.native="onCardClick"
        :class="{ 'cursor-pointer': !isEditing }">
        <v-card-text class="pt-2">
          <div
            :class="{
              'variant-card__pull-layout d-flex align-center': isPullMode,
            }">
            <div
              v-if="isPullMode"
              class="d-flex flex-column align-center shrink-0">
              <v-checkbox
                :input-value="eligibleForCreation"
                hide-details
                class="variant-card__eligible-checkbox mt-0"
                @change="updateEligibleForCreation" />
              <v-chip
                v-if="value && value.id"
                x-small
                color="success"
                text-color="white"
                class="mt-1">
                {{ $t('entities.products.inArena') }}
              </v-chip>
            </div>
            <div :class="isPullMode ? 'flex-grow-1 min-width-0' : ''">
              <div
                v-if="!isPullMode"
                class="d-flex justify-end align-center pb-2">
                <v-btn
                  v-if="canRemove && !canOnlyChangeStock && isEditing"
                  icon
                  small
                  :title="$t('miscellaneous.delete')"
                  :disabled="disabled"
                  @click="remove">
                  <v-icon small>mdi-delete</v-icon>
                </v-btn>
              </div>
              <v-form ref="form" lazy-validation>
                <div class="variant-fields-grid">
                  <div
                    class="variant-fields-grid__image image-upload-wrapper full-width">
                    <label
                      class="image-upload-box"
                      :class="{
                        disabled:
                          disabled ||
                          isUploadingImage ||
                          (!isEditing && !canOnlyChangeStock) ||
                          managerCoinsReadOnly ||
                          managerCoinsLimitedFields,
                        'has-image': localVariant.image || imagePreview,
                      }">
                      <input
                        type="file"
                        accept="image/*"
                        :disabled="
                          disabled ||
                          isUploadingImage ||
                          (canOnlyChangeStock ? true : !isEditing) ||
                          managerCoinsReadOnly ||
                          managerCoinsLimitedFields
                        "
                        @change="onImageSelected" />
                      <v-img
                        v-if="localVariant.image || imagePreview"
                        :src="imagePreview || localVariant.image"
                        width="100%"
                        height="100%"
                        cover></v-img>
                      <v-progress-circular
                        v-else-if="isUploadingImage"
                        indeterminate
                        color="primary" />
                      <v-icon v-else color="primary">mdi-image-plus</v-icon>
                    </label>
                  </div>
                  <div class="variant-fields-grid__top-fields">
                    <div class="variant-fields-grid__main-row">
                      <v-text-field
                        class="variant-fields-grid__name"
                        :value="localVariant.name"
                        :label="
                          withRequiredAsterisk($t('generalAttributes.name'))
                        "
                        :rules="[rules.required]"
                        outlined
                        dense
                        hide-details="auto"
                        :disabled="
                          disabled ||
                          canOnlyChangeStock ||
                          !isEditing ||
                          managerCoinsReadOnly
                        "
                        @input="updateField('name', $event)" />
                      <v-text-field
                        class="variant-fields-grid__sku"
                        :value="localVariant.sku"
                        :label="
                          withRequiredAsterisk(
                            $t('entities.products.attributes.sku'),
                          )
                        "
                        :rules="[rules.required]"
                        outlined
                        dense
                        hide-details="auto"
                        :disabled="
                          disabled ||
                          canOnlyChangeStock ||
                          !isEditing ||
                          managerCoinsReadOnly
                        "
                        @input="updateField('sku', $event)" />
                      <div
                        v-if="showStockIndicator"
                        class="stock-indicator d-flex align-center">
                        <div
                          class="stock-pill mr-2"
                          role="img"
                          :aria-label="stockText">
                          <div
                            class="stock-pill__fill"
                            :style="stockFillStyle"
                            :class="stockFillColorClass"></div>
                        </div>
                        <span class="stock-indicator__text">{{
                          stockText
                        }}</span>
                      </div>
                      <v-switch
                        class="active-toggle"
                        :input-value="localVariant.active"
                        :label="$t('entities.products.attributes.active')"
                        inset
                        dense
                        hide-details
                        :disabled="
                          disabled ||
                          canOnlyChangeStock ||
                          !isEditing ||
                          managerCoinsReadOnly
                        "
                        @change="updateField('active', $event)" />
                    </div>
                    <v-text-field
                      class="variant-fields-grid__price"
                      :value="primaryPrice.gross_price"
                      type="text"
                      inputmode="decimal"
                      :label="priceLabel"
                      :rules="[rules.required]"
                      outlined
                      dense
                      :prefix="currencyLabel"
                      :disabled="isPriceDisabled"
                      hide-details="auto"
                      @input="updatePriceAmount($event)" />
                  </div>
                  <div
                    v-if="isEditing"
                    class="variant-fields-grid__description">
                    <v-textarea
                      :value="localVariant.description"
                      :label="$t('generalAttributes.description')"
                      hide-details="auto"
                      rows="1"
                      outlined
                      dense
                      :disabled="
                        disabled ||
                        canOnlyChangeStock ||
                        !isEditing ||
                        (isManager &&
                          !isAdmin &&
                          (isCoinsCategory || isPassesCategory))
                      "
                      @input="updateField('description', $event)" />
                  </div>
                  <div class="variant-fields-grid__stock-actions">
                    <v-btn
                      v-if="isEditing && hasPersistedVariant && trackStock"
                      color="primary"
                      block
                      :disabled="
                        canOnlyChangeStock
                          ? disabled
                          : disabled ||
                            !isEditing ||
                            managerCoinsReadOnly ||
                            managerCoinsLimitedFields
                      "
                      @click="openStockTransactionsDialog">
                      {{ $t('entities.stockTransactions.title') }}
                    </v-btn>
                  </div>
                </div>
                <v-row dense>
                  <v-col v-if="isCoinsCategory" cols="12">
                    <v-row dense>
                      <v-col cols="12" md="6">
                        <v-select
                          :items="coinTypeOptions"
                          item-text="text"
                          item-value="value"
                          :value="coinType"
                          :label="$t('entities.products.attributes.coinType')"
                          hide-details="auto"
                          outlined
                          dense
                          :disabled="
                            disabled ||
                            canOnlyChangeStock ||
                            !isEditing ||
                            (isManager && !isAdmin)
                          "
                          @change="coinType = $event" />
                      </v-col>
                      <v-col cols="12" :md="isCoinsPack ? 4 : 6">
                        <v-text-field
                          :value="coinAmount"
                          type="number"
                          step="1"
                          min="0"
                          :label="$t('entities.products.attributes.coinAmount')"
                          hide-details="auto"
                          outlined
                          dense
                          :disabled="
                            disabled ||
                            canOnlyChangeStock ||
                            !isEditing ||
                            coinType === 'coins' ||
                            (isManager && !isAdmin)
                          "
                          @input="coinAmount = $event">
                          <template #prepend-inner>
                            <CoinIcon :size="18" />
                          </template>
                        </v-text-field>
                      </v-col>
                      <v-col v-if="isCoinsPack" cols="12" md="2">
                        <v-select
                          :items="packTypeOptions"
                          :value="localVariant.pack_type"
                          :label="$t('entities.products.attributes.packType')"
                          hide-details="auto"
                          outlined
                          dense
                          :disabled="
                            disabled ||
                            canOnlyChangeStock ||
                            !isEditing ||
                            (isManager && !isAdmin)
                          "
                          @change="updateField('pack_type', $event)" />
                      </v-col>
                    </v-row>
                  </v-col>
                  <v-col v-if="isPassesCategory" cols="12">
                    <v-row dense>
                      <v-col cols="12" md="2">
                        <v-menu
                          v-model="passValidFromMenu"
                          :close-on-content-click="false"
                          transition="scale-transition"
                          offset-y>
                          <template v-slot:activator="{ on, attrs }">
                            <v-text-field
                              v-bind="attrs"
                              :value="passValidFrom"
                              :label="
                                withRequiredAsterisk(
                                  $t('entities.products.attributes.validFrom'),
                                )
                              "
                              hide-details="auto"
                              :rules="[requiredRule]"
                              outlined
                              dense
                              readonly
                              :disabled="
                                disabled ||
                                canOnlyChangeStock ||
                                !isEditing ||
                                (isManager && !isAdmin)
                              "
                              v-on="on" />
                          </template>
                          <v-time-picker
                            v-model="passValidFrom"
                            format="24hr"
                            @input="passValidFromMenu = false" />
                        </v-menu>
                      </v-col>
                      <v-col cols="12" md="2">
                        <v-menu
                          v-model="passValidToMenu"
                          :close-on-content-click="false"
                          transition="scale-transition"
                          offset-y>
                          <template v-slot:activator="{ on, attrs }">
                            <v-text-field
                              v-bind="attrs"
                              :value="passValidTo"
                              :label="
                                withRequiredAsterisk(
                                  $t('entities.products.attributes.validTo'),
                                )
                              "
                              hide-details="auto"
                              :rules="[requiredRule]"
                              outlined
                              dense
                              readonly
                              :disabled="
                                disabled ||
                                canOnlyChangeStock ||
                                !isEditing ||
                                (isManager && !isAdmin)
                              "
                              v-on="on" />
                          </template>
                          <v-time-picker
                            v-model="passValidTo"
                            format="24hr"
                            @input="passValidToMenu = false" />
                        </v-menu>
                      </v-col>
                      <v-col cols="12" md="8">
                        <v-select
                          :items="weekdayOptions"
                          :value="passWeekdays"
                          :label="
                            withRequiredAsterisk(
                              $t('entities.products.attributes.weekdays'),
                            )
                          "
                          hide-details="auto"
                          :rules="[requiredRule]"
                          multiple
                          outlined
                          dense
                          :disabled="
                            disabled ||
                            canOnlyChangeStock ||
                            !isEditing ||
                            (isManager && !isAdmin)
                          "
                          @change="passWeekdays = $event" />
                      </v-col>
                    </v-row>
                  </v-col>
                </v-row>
              </v-form>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </template>
    <span>{{ $t('entities.products.onlyManagerCanEditVariants') }}</span>
  </v-tooltip>
</template>

<script>
import { COIN_TYPES, COIN_TYPE_LABELS } from '@/constants/coins'
import { PACK_TYPE_OPTIONS } from '@/constants/packs'
import { WEEKDAYS } from '@/constants/weekdays'
import CoinIcon from '@/components/CoinIcon.vue'
import { mapActions } from 'vuex'
import FilesApi from '@/services/misc/FilesApi'
import { StockAvailabilityThreshold } from '@/constants/stock'
import isRole from '@/mixins/crud/computed/isRole'

const defaultPrice = () => ({
  id: null,
  gross_price: '',
  currency_id: null,
})

const baseVariant = () => ({
  uid: null,
  name: '',
  description: '',
  sku: '',
  image: '',
  attributes: {},
  pack_type: null,
  active: false,
  sort_order: 1,
  prices: [defaultPrice()],
  stock: {
    available: null,
    waste: null,
  },
})

export default {
  name: 'VariantUpsertCard',
  components: {
    CoinIcon,
  },
  mixins: [isRole],
  props: {
    value: { type: Object, required: true },
    currencies: { type: Array, default: () => [] },
    isCoinsCategory: { type: Boolean, default: false },
    isPassesCategory: { type: Boolean, default: false },
    isEditing: { type: Boolean, default: false },
    canRemove: { type: Boolean, default: true },
    trackStock: { type: Boolean, default: true },
    canOnlyChangeStock: { type: Boolean, default: false },
    rules: {
      type: Object,
      default: () => ({
        required: (value) =>
          (typeof value === 'string' ? value.trim().length > 0 : !!value) || '',
      }),
    },
    disabled: { type: Boolean, default: false },
    index: { type: Number, default: 1 },
    isPullMode: { type: Boolean, default: false },
  },
  data() {
    return {
      localVariant: {
        ...baseVariant(),
        ...this.value,
      },
      imagePreview: '',
      imageFile: null,
      isUploadingImage: false,
      passValidFromMenu: false,
      passValidToMenu: false,
    }
  },
  computed: {
    requiredRule() {
      return (value) =>
        (Array.isArray(value)
          ? value.length > 0
          : typeof value === 'string'
          ? value.trim().length > 0
          : value !== null && value !== undefined && value !== '') ||
        this.$t('validation.required')
    },
    packTypeOptions() {
      return PACK_TYPE_OPTIONS
    },
    defaultCurrency() {
      return this.currencies[0]
    },
    currencyLabel() {
      return this.defaultCurrency?.symbol || this.defaultCurrency?.code || ''
    },
    stockAvailable() {
      const available = this.localVariant?.stock?.available
      if (available === null || available === undefined) {
        return null
      }
      const parsed =
        typeof available === 'number' ? available : Number(available)
      return Number.isNaN(parsed) ? null : parsed
    },
    hasStockIndicator() {
      return this.stockAvailable !== null
    },
    showStockIndicator() {
      return this.trackStock && this.hasStockIndicator
    },
    stockText() {
      if (!this.hasStockIndicator) {
        return ''
      }
      return this.$t('entities.products.inStockLabel', {
        stock: this.stockAvailable,
      })
    },
    stockFillRatio() {
      if (!this.hasStockIndicator || this.stockAvailable === null) {
        return 0
      }
      const ratio = this.stockAvailable / StockAvailabilityThreshold.UPPER
      if (Number.isNaN(ratio)) {
        return 0
      }
      return Math.max(0, Math.min(1, ratio))
    },
    stockFillStyle() {
      return {
        width: `${Math.round(this.stockFillRatio * 100)}%`,
      }
    },
    stockFillColorClass() {
      if (!this.hasStockIndicator || this.stockAvailable === null) {
        return ''
      }
      if (this.stockAvailable < StockAvailabilityThreshold.LOWER) {
        return 'stock-pill__fill--low'
      }
      if (this.stockAvailable <= StockAvailabilityThreshold.UPPER) {
        return 'stock-pill__fill--medium'
      }
      return 'stock-pill__fill--high'
    },
    primaryPrice() {
      if (!Array.isArray(this.localVariant.prices)) {
        return defaultPrice()
      }
      const [primary] = this.localVariant.prices
      return {
        id: primary?.id || null,
        gross_price:
          primary && primary.gross_price !== undefined
            ? primary.gross_price
            : '',
        currency_id:
          primary && primary.currency_id !== undefined
            ? primary.currency_id
            : this.defaultCurrency?.id,
      }
    },
    hasPersistedVariant() {
      return !!(this.localVariant?.id || this.localVariant?._id)
    },
    priceLabel() {
      const baseLabel = this.$t('generalAttributes.price')
      return this.withRequiredAsterisk(baseLabel)
    },
    managerRestrictedByCoinType() {
      return this.isManager && !this.isAdmin && this.isCoinsCategory
    },
    managerCoinsReadOnly() {
      return (
        this.managerRestrictedByCoinType && this.coinType === COIN_TYPES.coins
      )
    },
    managerCoinsLimitedFields() {
      return (
        this.managerRestrictedByCoinType &&
        (this.coinType === COIN_TYPES.apology_coins ||
          this.coinType === COIN_TYPES.reward_coins)
      )
    },
    managerCoinsPackFullAccess() {
      return (
        this.managerRestrictedByCoinType &&
        this.coinType === COIN_TYPES.coins_pack
      )
    },
    isPriceDisabled() {
      return (
        this.disabled ||
        this.canOnlyChangeStock ||
        !this.isEditing ||
        (this.managerRestrictedByCoinType && !this.managerCoinsPackFullAccess)
      )
    },
    coinTypeOptions() {
      const coinTypes = Object.values(COIN_TYPES)
      return coinTypes.map((it) => ({
        text: COIN_TYPE_LABELS[it],
        value: it,
      }))
    },
    coinType: {
      get() {
        return this.localVariant?.attributes?.type || 'coins'
      },
      set(value) {
        this.setCoinAttributes(value, this.coinAmount)
      },
    },
    coinAmount: {
      get() {
        const amount = this.localVariant?.attributes?.amount
        return amount === null || amount === undefined ? '' : amount
      },
      set(value) {
        const parsed = this.parseInteger(value)
        this.setCoinAttributes(this.coinType, parsed)
      },
    },
    weekdayOptions() {
      return WEEKDAYS.map((weekday) => ({
        text: this.$t(`miscellaneous.days.${weekday.name}`),
        value: weekday.value,
      }))
    },
    passValidFrom: {
      get() {
        return this.localVariant?.attributes?.valid_from || ''
      },
      set(value) {
        this.setPassAttributes(value, this.passValidTo, this.passWeekdays)
      },
    },
    passValidTo: {
      get() {
        return this.localVariant?.attributes?.valid_to || ''
      },
      set(value) {
        this.setPassAttributes(this.passValidFrom, value, this.passWeekdays)
      },
    },
    passWeekdays: {
      get() {
        const weekdays = this.localVariant?.attributes?.weekdays
        return Array.isArray(weekdays) ? weekdays : []
      },
      set(value) {
        this.setPassAttributes(this.passValidFrom, this.passValidTo, value)
      },
    },
    isCoinsPack() {
      return this.isCoinsCategory && this.coinType === COIN_TYPES.coins_pack
    },
    eligibleForCreation() {
      if (!this.isPullMode) return true
      return this.value?.eligibleForCreation === true
    },
  },
  watch: {
    value: {
      deep: true,
      handler(newValue) {
        this.localVariant = {
          ...baseVariant(),
          ...newValue,
        }
        this.imagePreview = ''
        this.imageFile = null
      },
    },
    isCoinsCategory: {
      immediate: true,
      handler(isCoins) {
        if (isCoins) {
          this.ensureCoinAttributes()
        } else {
          this.stripCoinAttributes()
        }
      },
    },
    isPassesCategory: {
      immediate: true,
      handler(isPasses) {
        if (isPasses) {
          this.ensurePassAttributes()
        } else {
          this.stripPassAttributes()
        }
      },
    },
    isEditing(value) {
      if (value) {
        if (this.isCoinsCategory) {
          this.ensureCoinAttributes()
        }
        if (this.isPassesCategory) {
          this.ensurePassAttributes()
        }
      }
    },
  },
  methods: {
    ...mapActions(['showSnackbar']),
    onCardClick() {
      if (this.isEditing) {
        return
      }
      this.$emit('edit-toggle', !this.isEditing)
    },
    updateField(field, value) {
      if (!this.isEditing) return
      this.localVariant = {
        ...this.localVariant,
        [field]: value,
      }
      this.emitUpdate()
      this.emitChange()
    },
    updatePriceAmount(value) {
      if (!this.isEditing) return
      const normalized =
        typeof value === 'string' ? value.replace(',', '.') : value
      const parsedValue =
        normalized === '' || normalized === null || normalized === undefined
          ? ''
          : Number(normalized)
      const grossPrice = Number.isNaN(parsedValue) ? '' : parsedValue
      this.updatePrimaryPrice({ gross_price: grossPrice })
    },
    updatePriceCurrency(currencyId) {
      if (!this.isEditing) return
      this.updatePrimaryPrice({ currency_id: currencyId })
    },
    updatePrimaryPrice(partial) {
      const existingPrices = Array.isArray(this.localVariant.prices)
        ? [...this.localVariant.prices]
        : []
      const [primary = defaultPrice()] = existingPrices
      const updatedPrimary = { ...primary, ...partial }
      existingPrices[0] = updatedPrimary
      this.updateField('prices', existingPrices)
    },
    ensureCoinAttributes() {
      const currentType = this.localVariant?.attributes?.type
      const currentAmount = this.localVariant?.attributes?.amount
      const desiredType = currentType || 'coins'
      this.setCoinAttributes(desiredType, currentAmount ?? '')
    },
    stripCoinAttributes() {
      const attributes = { ...(this.localVariant?.attributes || {}) }
      let changed = false
      if ('type' in attributes) {
        delete attributes.type
        changed = true
      }
      if ('amount' in attributes) {
        delete attributes.amount
        changed = true
      }
      if (changed) {
        this.updateField('attributes', attributes)
      }
    },
    setCoinAttributes(type, amount) {
      if (!this.isCoinsCategory) {
        return
      }
      const attributes = { ...(this.localVariant?.attributes || {}) }
      delete attributes.type
      delete attributes.amount
      if (type) {
        attributes.type = type
      }
      if (type !== 'coins' && amount !== '' && amount !== null) {
        attributes.amount = amount
      }
      this.updateField('attributes', attributes)
    },
    parseInteger(value) {
      if (value === '' || value === null || value === undefined) {
        return ''
      }
      const parsed = parseInt(value, 10)
      return Number.isNaN(parsed) ? '' : parsed
    },
    ensurePassAttributes() {
      const currentFrom = this.localVariant?.attributes?.valid_from || ''
      const currentTo = this.localVariant?.attributes?.valid_to || ''
      const currentWeekdays = this.localVariant?.attributes?.weekdays || []
      this.setPassAttributes(currentFrom, currentTo, currentWeekdays)
    },
    stripPassAttributes() {
      const attributes = { ...(this.localVariant?.attributes || {}) }
      let changed = false
      if ('valid_from' in attributes) {
        delete attributes.valid_from
        changed = true
      }
      if ('valid_to' in attributes) {
        delete attributes.valid_to
        changed = true
      }
      if ('weekdays' in attributes) {
        delete attributes.weekdays
        changed = true
      }
      if (changed) {
        this.updateField('attributes', attributes)
      }
    },
    setPassAttributes(validFrom, validTo, weekdays) {
      if (!this.isPassesCategory) {
        return
      }
      const attributes = { ...(this.localVariant?.attributes || {}) }
      delete attributes.valid_from
      delete attributes.valid_to
      delete attributes.weekdays
      if (validFrom !== undefined) {
        attributes.valid_from = validFrom
      }
      if (validTo !== undefined) {
        attributes.valid_to = validTo
      }
      if (Array.isArray(weekdays)) {
        attributes.weekdays = weekdays
      }
      this.updateField('attributes', attributes)
    },
    emitUpdate() {
      this.$emit('input', { ...this.localVariant })
    },
    emitChange() {
      this.$emit('change')
    },
    withRequiredAsterisk(label) {
      return `${label} *`
    },
    remove() {
      this.$emit('remove')
    },
    updateEligibleForCreation(checked) {
      this.$emit('input', {
        ...this.localVariant,
        eligibleForCreation: !!checked,
      })
    },
    validate() {
      const form = this.$refs.form
      if (form && typeof form.validate === 'function') {
        return form.validate()
      }
      return true
    },
    resetValidation() {
      const form = this.$refs.form
      if (form && typeof form.resetValidation === 'function') {
        form.resetValidation()
      }
    },
    async onImageSelected(event) {
      const [file] = event.target.files || []
      if (!file) {
        this.imageFile = null
        this.imagePreview = ''
        return
      }
      if (!this.isEditing) return
      this.imageFile = file
      this.imagePreview = URL.createObjectURL(file)
      await this.uploadSelectedImage()
    },
    async uploadSelectedImage() {
      if (!this.imageFile) {
        return
      }
      this.isUploadingImage = true
      try {
        const response = await FilesApi.upload('arenas', this.imageFile)
        this.imageFile = null
        if (!response || response.status !== 200 || !response.data?.link) {
          this.notifyUploadError()
          return
        }
        this.imagePreview = ''
        this.updateField('image', response.data.link)
      } catch (error) {
        console.error(error)
        this.notifyUploadError()
      } finally {
        this.isUploadingImage = false
      }
    },
    notifyUploadError() {
      this.showSnackbar({
        color: 'error',
        message: this.$t('miscellaneous.fileUploadFailed'),
      })
    },
    openStockTransactionsDialog() {
      if (this.disabled) {
        return
      }
      if (!this.canOnlyChangeStock && !this.isEditing) {
        return
      }
      this.$emit('stock-transactions:open', {
        ...this.localVariant,
      })
    },
  },
}
</script>

<style scoped>
.variant-card {
  border-color: #e0e0e0;
}

.variant-card__eligible-checkbox {
  flex-shrink: 0;
}

.variant-fields-grid {
  display: grid;
  grid-template-columns: auto 1fr auto;
  grid-template-rows: auto auto;
  gap: 8px;
  margin-bottom: 16px;
}

.variant-fields-grid__image {
  grid-column: 1;
  grid-row: 1 / -1;
  align-self: start;
}

.variant-fields-grid__top-fields {
  grid-column: 2 / -1;
  grid-row: 1;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  min-width: 0;
}

.variant-fields-grid__top-fields .variant-fields-grid__main-row {
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

.variant-fields-grid__main-row .variant-fields-grid__name {
  flex: 1 1 0;
  min-width: 0;
}

.variant-fields-grid__main-row .variant-fields-grid__sku {
  flex: 0 1 120px;
  min-width: 0;
}

.variant-fields-grid__top-fields .variant-fields-grid__price {
  flex: 0 0 auto;
  max-width: 88px;
}

.variant-fields-grid__description {
  grid-column: 2;
  grid-row: 2;
  min-width: 0;
}

.variant-fields-grid__stock-actions {
  grid-column: 3;
  grid-row: 2;
  align-self: start;
}

@media (max-width: 959px) {
  .variant-fields-grid {
    grid-template-columns: 1fr;
    grid-template-rows: auto;
  }

  .variant-fields-grid__image {
    grid-column: 1;
    grid-row: auto;
  }

  .variant-fields-grid__top-fields {
    grid-column: 1;
    grid-row: auto;
    flex-wrap: wrap;
  }

  .variant-fields-grid__top-fields .variant-fields-grid__price {
    max-width: none;
  }

  .variant-fields-grid__description {
    grid-column: 1;
    grid-row: auto;
  }

  .variant-fields-grid__stock-actions {
    grid-column: 1;
    grid-row: auto;
  }
}

.cursor-pointer {
  cursor: pointer;
}

.image-upload-wrapper.full-width {
  width: 100%;
  height: auto;
}

.image-upload-box {
  position: relative;
  width: 88px;
  height: 88px;
  border: 1px dashed var(--v-primary-base);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  overflow: hidden;
}

.image-upload-box.has-image {
  border: none;
}

.image-upload-box input[type='file'] {
  position: absolute;
  inset: 0;
  opacity: 0;
  cursor: pointer;
}

.image-upload-box.disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.stock-indicator__text {
  font-weight: 600;
  font-size: 0.875rem;
  color: rgba(0, 0, 0, 0.87);
  white-space: nowrap;
}

.stock-pill {
  width: 40px;
  height: 10px;
  border-radius: 999px;
  background-color: rgba(0, 0, 0, 0.12);
  overflow: hidden;
  flex-shrink: 0;
}

.stock-pill__fill {
  height: 100%;
  transition: width 0.2s ease;
  background-color: var(--v-success-base, #4caf50);
}

.stock-pill__fill--low {
  background-color: #f44336;
}

.stock-pill__fill--medium {
  background-color: #ff9800;
}

.stock-pill__fill--high {
  background-color: #4caf50;
}

.active-toggle {
  margin-top: 0.375rem;
  margin-left: 0.25rem;
  margin-bottom: 6px;
}
</style>
